/**
 * メールテンプレートで使えるシステム変数（単一の出典）
 * ------------------------------------------------------------------
 * 置換キーは SendGrid の personalizations[].substitutions に渡すキーと一致させる。
 * サーバー側の実装（rcdr_server/routes/sendMail.js の substitutions 構築）と
 * クライアント即時発送（SurveyMailSendPage.js）がキーの原本。ここは表示・検証用の写し。
 * ★キーを追加・削除したら本ファイルと .claude/rules/external-services.md「SendGrid」節を同期すること。
 *
 * 利用箇所:
 *   - MakeTemplatePage: 変数チェック（MailVariableCheckPanel）/ 変数の挿入（MailVariablePopover）
 *
 * 回答差戻し通知（MailTemplate.system_key='revert_notify'）は専用の変数セット（REVERT_NOTIFY_VARIABLES）で
 * 検証する。原本は rcdr_server/routes/surveyToken.js の差戻し通知（buildRevertNotifyVars）。
 *
 * 経緯: 2026-09 に本番テンプレートで {$lastname$} {$name$}（DB フィールド名）が変数として使われ、
 *       置換されないまま配信された。有効な変数を編集画面で確実に分かるようにするため導入。
 */

// 変数グループ → 表示ラベル（Popover の小見出し・チェック結果のツールチップ）
export const MAIL_VARIABLE_GROUPS = {
  user: {
    label: '受講者情報',
    note: 'サーベイお知らせ配信で常に置換されます。',
    conditional: false,
  },
  season: {
    label: 'シーズン情報（条件付き）',
    note:
      'サーベイシーズンが指定された予約・即時発送でのみ値が入ります。それ以外は空文字になります。',
    conditional: true,
  },
  target360: {
    label: '360度診断の協力者・全体配信（条件付き）',
    note:
      '360度診断の協力者用・全体配信でのみ値が入ります。それ以外は空文字になります。',
    conditional: true,
  },
  revert: {
    label: '回答差戻し通知（専用）',
    note: '回答差戻し通知メールでのみ置換されます。',
    conditional: false,
  },
  csv: {
    label: 'CSVから配信（固定列）',
    note:
      'CSVから配信でのみ使えます。サーベイお知らせ配信では置換されません。',
    conditional: true,
  },
};

// サーベイお知らせ配信で使えるシステム変数
export const MAIL_VARIABLES = [
  { key: 'company', label: '社名', group: 'user' },
  { key: 'fullname', label: 'フルネーム（姓 名）', group: 'user' },
  { key: 'familyname', label: '苗字（姓）', group: 'user' },
  { key: 'email', label: 'メール', group: 'user' },
  { key: 'tname', label: '研修名（シーズン名）', group: 'user' },
  { key: 'emp_no', label: '社員番号', group: 'user' },
  { key: 'init_pass', label: '初期パスワード', group: 'user' },
  { key: 'department', label: '部署名（第1階層）', group: 'user' },
  {
    key: 'department1',
    label: '部署名（第1階層・department と同値）',
    group: 'user',
  },
  { key: 'department2', label: '部署名（第2階層）', group: 'user' },
  { key: 'department3', label: '部署名（第3階層）', group: 'user' },
  { key: 'department4', label: '部署名（第4階層）', group: 'user' },
  { key: 'department5', label: '部署名（第5階層）', group: 'user' },
  { key: 'department6', label: '部署名（第6階層）', group: 'user' },
  { key: 'department7', label: '部署名（第7階層）', group: 'user' },
  // サーベイシーズン情報を参照する変数（external-services.md「サーベイシーズン由来変数」）
  {
    key: 'survey_display_name',
    label: '診断表示名',
    group: 'season',
    example: '●●診断',
  },
  {
    key: 'first_delivery_date',
    label: '初回配信日',
    group: 'season',
    example: '#月#日(#)',
  },
  {
    key: 'due_datetime',
    label: '回答期限',
    group: 'season',
    example: '202#年#月#日（#）##:##',
  },
  {
    key: 'extended_due_datetime',
    label: '回答延長期限',
    group: 'season',
    example: '202#年#月#日（#）##:##',
  },
  {
    key: 'multiple_choice_count',
    label: '選択式設問数',
    group: 'season',
    example: '00',
  },
  {
    key: 'free_text_count',
    label: '自由記述式設問数',
    group: 'season',
    example: '00',
  },
  {
    key: 'time_required',
    label: '回答所要時間（分）',
    group: 'season',
    example: '15',
  },
  // 360度診断の協力者・全体配信でのみ実値
  { key: 'target', label: '対象者の氏名', group: 'target360' },
];

// 編集できるかどうかは edit_ok だけで決める（false = 閲覧のみ・編集／複製不可。2026-10-21 決定）。
// サーバー routes/mailTemplate.js の isLockedTemplate と同じ判定。
export const isLockedMailTemplate = (t) => !!t && t.edit_ok === false;
// 削除できないテンプレート: 編集不可（edit_ok:false）か、システムが使う（system_key あり）もの。
// サーバー DELETE /api/mailTemplate/:mtId と同じ判定。
export const isUndeletableMailTemplate = (t) =>
  !!t && (t.edit_ok === false || !!t.system_key);

// 回答差戻し通知の固定テンプレート（MailTemplate.system_key）と、そのテンプレートで使える変数。
// サーバーの差戻し通知はこの 5 つだけを置換する（サーベイお知らせ配信の変数は置換されない）。
export const REVERT_NOTIFY_SYSTEM_KEY = 'revert_notify';
export const REVERT_NOTIFY_VARIABLES = [
  { key: 'fullname', label: 'フルネーム（姓 名）', group: 'revert' },
  { key: 'email', label: 'メール（ログインID）', group: 'revert' },
  {
    key: 'survey_line',
    label: '対象サーベイ（マイ・サーベイと同じ表示名）',
    group: 'revert',
    example: '[本人向け]●●診断（対象者：新宿 太郎 様）',
  },
  {
    key: 'due_line',
    label: '回答期限の一文（期限未設定なら空行）',
    group: 'revert',
    example: '回答期限（202#年#月#日（#）##:##）までにご提出ください。',
  },
  {
    key: 'mysurvey_url',
    label: 'マイ・サーベイのURL（送信した環境）',
    group: 'revert',
  },
];

// CSVから配信で固定的に使う列名。それ以外の列名は CSV 側の定義に従うため検証対象外（未定義扱いだが保存は可能）。
export const CSV_FIXED_VARIABLES = [
  { key: 'email', label: 'メール（第1列・必須）', group: 'csv' },
  { key: 'cc', label: '個別参照（ユーザ毎に別の cc）', group: 'csv' },
];

// よくある誤記 → 正しい変数の提案（DB フィールド名との混同を吸収）
export const MAIL_VARIABLE_SUGGESTIONS = {
  lastname: ['familyname', 'fullname'],
  last_name: ['familyname', 'fullname'],
  name: ['fullname', 'familyname'],
  firstname: ['fullname'],
  first_name: ['fullname'],
  username: ['fullname'],
  user_name: ['fullname'],
  full_name: ['fullname'],
  family_name: ['familyname'],
  mail: ['email'],
  mailaddress: ['email'],
  mail_address: ['email'],
  password: ['init_pass'],
  pass: ['init_pass'],
  initpass: ['init_pass'],
  season_name: ['tname'],
  t_name: ['tname'],
  survey_name: ['survey_display_name'],
  name_360: ['survey_display_name'],
  dept: ['department'],
  department0: ['department'],
  empno: ['emp_no'],
  employee_no: ['emp_no'],
  due_date: ['due_datetime'],
  deadline: ['due_datetime'],
};

// key → 定義（MAIL_VARIABLES 優先。cc のみ CSV 固定列から）
const VARIABLE_MAP = (() => {
  const map = new Map();
  MAIL_VARIABLES.forEach((v) => map.set(v.key, v));
  CSV_FIXED_VARIABLES.forEach((v) => {
    if (!map.has(v.key)) map.set(v.key, v);
  });
  return map;
})();

// 回答差戻し通知テンプレート用の key → 定義
const REVERT_VARIABLE_MAP = new Map(
  REVERT_NOTIFY_VARIABLES.map((v) => [v.key, v])
);

/** 定義済み変数か */
export const isKnownVariable = (key) => VARIABLE_MAP.has(key);

/** 変数定義を返す（未定義は undefined） */
export const getVariableDef = (key) => VARIABLE_MAP.get(key);

// 正常形: {$key$}（key は英数字とアンダースコア）
export const VALID_TAG_RE = /\{\$([A-Za-z0-9_]+)\$\}/g;

// 形式が崩れた候補（正常形を除去した残りに順に適用する）。
// どの配信経路でも置換されないため「形式エラー」として保存を止める。
const MALFORMED_PATTERNS = [
  // {$name} / {$ name $} / {$name $} / {$$} / {$na-me$} … {$ で始まり } で閉じるが正常形でない
  /\{\$[^{}\n]*?\}/g,
  // {name$}
  /\{[^{}\n$]*\$\}/g,
  // ${name}（JS テンプレート記法の混同）
  /\$\{[^{}\n]*\}/g,
  // {{name}}（Mustache 記法の混同）
  /\{\{[^{}\n]*\}\}/g,
  // {%name%}（旧サンプルの記法）
  /\{%[^{}\n]*%\}/g,
  // 閉じ忘れ: {$name$ / {$name
  /\{\$[^\s{}]*/g,
  // 開き忘れ: name$}
  /[^\s{}]*\$\}/g,
];

/** 文字列中の re のマッチを収集し、同じ長さの空白に置き換えて返す（重複検出防止） */
const blankOut = (text, re) => {
  const matches = [];
  const blanked = text.replace(re, (m) => {
    matches.push(m);
    return ' '.repeat(m.length);
  });
  return { blanked, matches };
};

/** 2 文字列の編集距離（提案候補の近似一致用・短い変数名のため単純実装で十分） */
const levenshtein = (a, b) => {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i += 1) {
    const cur = [i];
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    prev = cur;
  }
  return prev[n];
};

/**
 * 未定義キーに対する正しい変数の候補を返す。
 * 明示マップ（MAIL_VARIABLE_SUGGESTIONS）を優先し、無ければ編集距離 2 以内の定義済みキーを返す。
 * @param {string} key
 * @returns {string[]}
 */
export const suggestVariables = (key, variableMap = VARIABLE_MAP) => {
  const lower = String(key || '').toLowerCase();
  // 明示マップの候補も、検証中の変数セットに無いものは出さない（差戻し通知では置換されない変数を勧めない）
  const explicit = (MAIL_VARIABLE_SUGGESTIONS[lower] || []).filter((k) =>
    variableMap.has(k)
  );
  if (explicit.length > 0) return explicit;
  if (lower.length < 4) return [];
  const near = [];
  variableMap.forEach((_def, known) => {
    const d = levenshtein(lower, known.toLowerCase());
    if (d <= 2) near.push({ known, d });
  });
  near.sort((x, y) => x.d - y.d || x.known.localeCompare(y.known));
  return near.slice(0, 3).map((x) => x.known);
};

/** 1 フィールド分の解析 */
const analyzeField = (text) => {
  const src = typeof text === 'string' ? text : '';
  const validKeys = [];
  const { blanked: afterValid } = blankOut(src, VALID_TAG_RE);
  // 正常形のキーを収集（blankOut はキーを返さないため再走査）
  src.replace(VALID_TAG_RE, (_m, key) => {
    validKeys.push(key);
    return _m;
  });
  let rest = afterValid;
  const malformed = [];
  MALFORMED_PATTERNS.forEach((re) => {
    const r = blankOut(rest, re);
    rest = r.blanked;
    malformed.push(...r.matches.map((m) => m.trim()).filter(Boolean));
  });
  return { validKeys, malformed };
};

/** key ごとの出現回数と出現フィールドを集約するヘルパー */
const accumulate = (map, key, field, extra) => {
  if (!map.has(key)) {
    map.set(key, { count: 0, fields: [], ...extra });
  }
  const entry = map.get(key);
  entry.count += 1;
  if (!entry.fields.includes(field)) entry.fields.push(field);
};

/**
 * 件名・本文の変数を解析し、定義済み／条件付き／未定義／形式エラーに分類する。
 *
 * @param {string} subject 件名
 * @param {string} body    本文
 * @param {object} [options]
 * @param {string} [options.systemKey] 編集中テンプレートの system_key。
 *   REVERT_NOTIFY_SYSTEM_KEY のときは差戻し通知の変数セットだけを定義済みとして検証する。
 * @returns {{
 *   valid:       Array<{key:string,label:string,group:string,count:number,fields:string[]}>,
 *   conditional: Array<{key:string,label:string,group:string,count:number,fields:string[]}>,
 *   unknown:     Array<{key:string,count:number,fields:string[],suggestions:string[]}>,
 *   malformed:   Array<{raw:string,count:number,fields:string[]}>,
 *   total:       number,   // 正常形タグの総数（未定義含む）
 *   hasIssues:   boolean,  // 未定義または形式エラーあり
 *   isBlocked:   boolean,  // 形式エラーあり（保存不可）
 *   isRevertNotify: boolean, // 差戻し通知の変数セットで検証したか
 * }}
 */
export const analyzeMailVariables = (subject, body, options = {}) => {
  const isRevertNotify = options.systemKey === REVERT_NOTIFY_SYSTEM_KEY;
  const variableMap = isRevertNotify ? REVERT_VARIABLE_MAP : VARIABLE_MAP;
  const fields = [
    { name: '件名', text: subject },
    { name: '本文', text: body },
  ];
  const validMap = new Map();
  const conditionalMap = new Map();
  const unknownMap = new Map();
  const malformedMap = new Map();
  let total = 0;

  fields.forEach(({ name, text }) => {
    const { validKeys, malformed } = analyzeField(text);
    total += validKeys.length;
    validKeys.forEach((key) => {
      const def = variableMap.get(key);
      if (!def) {
        accumulate(unknownMap, key, name, {
          key,
          suggestions: suggestVariables(key, variableMap),
        });
      } else if (MAIL_VARIABLE_GROUPS[def.group]?.conditional) {
        accumulate(conditionalMap, key, name, {
          key,
          label: def.label,
          group: def.group,
        });
      } else {
        accumulate(validMap, key, name, {
          key,
          label: def.label,
          group: def.group,
        });
      }
    });
    malformed.forEach((raw) => {
      accumulate(malformedMap, raw, name, { raw });
    });
  });

  const unknown = Array.from(unknownMap.values());
  const malformed = Array.from(malformedMap.values());
  return {
    valid: Array.from(validMap.values()),
    conditional: Array.from(conditionalMap.values()),
    unknown,
    malformed,
    total,
    hasIssues: unknown.length > 0 || malformed.length > 0,
    isBlocked: malformed.length > 0,
    isRevertNotify,
  };
};

/**
 * テキスト中の {$fromKey$} を {$toKey$} に一括置換する。
 * @returns {{ text: string, count: number }}
 */
export const replaceVariable = (text, fromKey, toKey) => {
  const src = typeof text === 'string' ? text : '';
  const escaped = String(fromKey).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`\\{\\$${escaped}\\$\\}`, 'g');
  let count = 0;
  const out = src.replace(re, () => {
    count += 1;
    return `{$${toKey}$}`;
  });
  return { text: out, count };
};

/** 変数名 → {$key$} 表記 */
export const toTag = (key) => `{$${key}$}`;
